import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useSettings } from "../../lib/settings";
import { useAuth } from "../../lib/auth";
import { useOfficeFilter } from "../../lib/officeFilter";
import { useRealtimeEvent } from "../../lib/realtime";
import {
  ArrowUpRight,
  Plus,
  Settings,
  ChevronDown,
  Sparkles,
  X,
  Play,
  Stethoscope,
  FileText,
  Wallet,
  Send,
  Activity,
  Phone,
  Mail,
  Cake,
} from "@/components/icons";
import { toast } from "sonner";
import Portal from "../../components/Portal";
import AppleKpi from "../../components/AppleKpi";
import { todayLocal } from "../../lib/dateLocal";

const PERIOD_OPTIONS = [
  { value: "month", label: "Este mes", days: 30 },
  { value: "week", label: "Esta semana", days: 7 },
  { value: "day", label: "Hoy", days: 1 },
];
const periodLabel = (value) =>
  PERIOD_OPTIONS.find((p) => p.value === value)?.label || "Este mes";
const periodDays = (value) =>
  PERIOD_OPTIONS.find((p) => p.value === value)?.days || 30;

/**
 * Doctor Overview — "Lender Workspace" inspired layout (Feb 2026).
 *
 * Estructura:
 *   Header con título + acciones
 *   Row 1: 3 métricas grandes con tendencia + chart pequeño
 *   Card lateral: Próximas citas (chat-like) + Últimos pacientes + Comisión mes
 *   Workflow: 4 widgets bottom (Consultas / Recetas / Pagos / Referidos)
 */
export default function DoctorOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [appts, setAppts] = useState([]);
  const [patients, setPatients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const { pharmacyEnabled, pharmacyEnabledAt, reload } = useSettings();
  const { officeId, setOfficeId } = useOfficeFilter();
  const [offices, setOffices] = useState([]);
  const [welcome, setWelcome] = useState(null);
  const [workflowPeriod, setWorkflowPeriod] = useState("month");
  const [activityPeriod, setActivityPeriod] = useState("month");

  const reloadAll = () => {
    api.get("/doctor/stats").then((r) => setStats(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/payments").then((r) => setPayments(r.data));
  };

  useEffect(() => {
    api.get("/doctor/stats").then((r) => setStats(r.data));
    api.get("/appointments").then((r) => setAppts(r.data));
    api.get("/patients").then((r) => setPatients(r.data));
    api.get("/payments").then((r) => setPayments(r.data));
    api.get("/prescriptions").then((r) => setPrescriptions(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setReferrals(r.data)).catch(() => {});
    api.get("/diagnoses").then((r) => setDiagnoses(r.data)).catch(() => {});
    api.get("/offices").then((r) => setOffices(r.data || [])).catch(() => {});
  }, []);

  useRealtimeEvent("payment:updated", () => {
    api.get("/payments").then((r) => setPayments(r.data));
  });
  useRealtimeEvent("payment:created", () => {
    api.get("/payments").then((r) => setPayments(r.data));
  });

  const DISMISS_KEY = "eme_pharmacy_welcome_dismissed_at";
  useEffect(() => {
    if (!pharmacyEnabled || !pharmacyEnabledAt) return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed || dismissed < pharmacyEnabledAt) {
      setWelcome({
        title: "¡Bienvenido al envío digital de recetas!",
        message:
          "Ahora puedes enviar tus recetas directamente a las farmacias y generar ingresos por cada receta despachada.",
      });
    }
  }, [pharmacyEnabled, pharmacyEnabledAt]);
  useRealtimeEvent("pharmacy:welcome", (m) => {
    setWelcome({
      title: m?.title || "¡Bienvenido!",
      message: m?.message || "Ahora puedes enviar recetas digitalmente.",
    });
    reload();
  });
  const dismissWelcome = () => {
    setWelcome(null);
    localStorage.setItem(
      DISMISS_KEY,
      pharmacyEnabledAt || new Date().toISOString()
    );
  };

  // ── Derived data ─────────────────────────────────────────────────────────
  const derived = useMemo(() => {
    const now = new Date();
    const todayStr = todayLocal();
    const daysAgo = (n) => {
      const d = new Date();
      d.setDate(d.getDate() - n);
      return d;
    };

    const wfDays = periodDays(workflowPeriod);
    const actDays = periodDays(activityPeriod);

    const todayAppts = appts
      .filter((a) => a.date.slice(0, 10) === todayStr)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    const upcomingAppts = appts
      .filter((a) => new Date(a.date) >= now && a.status !== "cancelled")
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 5);

    // Trend windows — comparamos ventana actual vs ventana anterior.
    const curFrom = daysAgo(wfDays);
    const prevFrom = daysAgo(wfDays * 2);
    const inRangeCreated = (created, from, to) =>
      created && new Date(created) >= from && new Date(created) <= to;

    // Citas: contamos por fecha de la cita (incluye pasadas y futuras del rango)
    const inFwd = new Date();
    inFwd.setDate(inFwd.getDate() + wfDays);
    const apptsCur = appts.filter((a) => {
      const d = new Date(a.date);
      return d >= curFrom && d <= inFwd;
    }).length;
    const apptsPrev = appts.filter((a) => {
      const d = new Date(a.date);
      return d >= prevFrom && d < curFrom;
    }).length;

    // Pacientes nuevos / recetas — por created_at.
    const patsCur = patients.filter((p) =>
      inRangeCreated(p.created_at, curFrom, now)
    ).length;
    const patsPrev = patients.filter((p) =>
      inRangeCreated(p.created_at, prevFrom, curFrom)
    ).length;
    const rxCur = prescriptions.filter((r) =>
      inRangeCreated(r.created_at, curFrom, now)
    ).length;
    const rxPrev = prescriptions.filter((r) =>
      inRangeCreated(r.created_at, prevFrom, curFrom)
    ).length;
    const pct = (cur, prev) => {
      if (!prev) return cur > 0 ? 100 : 0;
      return Math.round(((cur - prev) / prev) * 1000) / 10;
    };

    // 7 buckets — actividad de la ventana workflow.
    const buckets = [];
    const bucketSize = Math.max(1, Math.round(wfDays / 7));
    for (let i = 6; i >= 0; i--) {
      const from = daysAgo((i + 1) * bucketSize);
      const to = daysAgo(i * bucketSize);
      const bA = appts.filter((a) => {
        const d = new Date(a.date);
        return d >= from && d < to;
      }).length;
      const bR = prescriptions.filter((r) =>
        inRangeCreated(r.created_at, from, to)
      ).length;
      buckets.push({ appts: bA, rx: bR });
    }
    const maxB = Math.max(1, ...buckets.map((b) => Math.max(b.appts, b.rx)));

    // Buckets amplios para el chart de Actividad (según activityPeriod).
    // month → 180 días, week → 30 días, day → 7 días (1 barra por día).
    const wideRange =
      activityPeriod === "day" ? 7 : activityPeriod === "week" ? 30 : 180;
    const wideBuckets = [];
    const monthLabels = [];
    let lastMonth = -1;
    for (let i = wideRange - 1; i >= 0; i--) {
      const from = daysAgo(i + 1);
      const to = daysAgo(i);
      const bA = appts.filter((a) => {
        const d = new Date(a.date);
        return d >= from && d < to;
      }).length;
      const bR = prescriptions.filter((r) =>
        inRangeCreated(r.created_at, from, to)
      ).length;
      wideBuckets.push({ appts: bA, rx: bR });
      const m = from.getMonth();
      if (m !== lastMonth) {
        monthLabels.push({
          idx: wideRange - 1 - i,
          label: from.toLocaleDateString("es-ES", {
            month: activityPeriod === "day" ? undefined : "short",
            day: activityPeriod === "day" ? "numeric" : undefined,
          }),
        });
        lastMonth = m;
      }
    }
    const wideMaxA = Math.max(1, ...wideBuckets.map((b) => b.appts));
    const wideMaxR = Math.max(1, ...wideBuckets.map((b) => b.rx));

    // Recent patients (top 5 by created_at)
    const recentPatients = [...patients]
      .sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      )
      .slice(0, 6);

    // Consultations gauge: completed today vs scheduled today
    const doneToday = todayAppts.filter(
      (a) => a.status === "completed" || a.status === "finished"
    ).length;
    const gaugePct = todayAppts.length
      ? Math.round((doneToday / todayAppts.length) * 100)
      : 0;

    // Prescriptions: last 4 weeks buckets
    const rxWeeks = [];
    for (let w = 3; w >= 0; w--) {
      const from = daysAgo((w + 1) * 7);
      const to = daysAgo(w * 7);
      rxWeeks.push(
        prescriptions.filter((r) => inRangeCreated(r.created_at, from, to)).length
      );
    }
    const rxMax = Math.max(1, ...rxWeeks);

    // Payments: paid vs pending
    const paid = payments
      .filter((p) => p.status === "paid")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const pending = payments
      .filter((p) => p.status !== "paid")
      .reduce((s, p) => s + (Number(p.amount) || 0), 0);

    // Referrals: pending/active
    const refActive = referrals.filter(
      (r) => r.status === "pending" || r.status === "accepted"
    ).length;
    const refTotal = referrals.length;

    // Top diseases — agrupar por diagnosis text (o cie10 si existe)
    const dxMap = new Map();
    for (const d of diagnoses) {
      const key = (d.diagnosis || d.cie10 || "Sin especificar").trim();
      if (!key) continue;
      if (!dxMap.has(key)) dxMap.set(key, { label: key, patients: new Set(), count: 0 });
      const entry = dxMap.get(key);
      entry.count += 1;
      if (d.patient_id) entry.patients.add(d.patient_id);
    }
    const topDx = Array.from(dxMap.values())
      .map((d) => ({ label: d.label, count: d.count, patients: d.patients.size }))
      .sort((a, b) => b.patients - a.patients)
      .slice(0, 5);
    const topDxMax = Math.max(1, ...topDx.map((d) => d.patients));

    return {
      todayAppts,
      upcomingAppts,
      apptsCur,
      trendAppts: pct(apptsCur, apptsPrev),
      patsCur,
      trendPats: pct(patsCur, patsPrev),
      rxCur,
      trendRx: pct(rxCur, rxPrev),
      buckets,
      maxB,
      wideBuckets,
      wideMaxA,
      wideMaxR,
      monthLabels,
      recentPatients,
      gaugePct,
      doneToday,
      rxWeeks,
      rxMax,
      paid,
      pending,
      refActive,
      refTotal,
      topDx,
      topDxMax,
    };
  }, [appts, patients, payments, prescriptions, referrals, diagnoses, workflowPeriod, activityPeriod]);

  if (!stats)
    return <div className="text-black/50 text-sm">Cargando…</div>;

  const missingBrand = [
    !user?.clinic_logo_url && "logo",
    !user?.stamp_url && "sello",
    !user?.signature_url && "firma",
  ].filter(Boolean);

  return (
    <div className="space-y-3 flex-1 flex flex-col min-h-0" data-testid="doctor-overview">
      {/* Alerta: faltan activos de marca (logo/sello/firma) */}
      {missingBrand.length > 0 && (
        <button
          type="button"
          onClick={() => navigate("/perfil?tab=brand")}
          data-testid="dashboard-brand-missing-cta"
          className="w-full text-left rounded-[16px] bg-white border border-black/[0.06] shadow-[0_1px_2px_rgba(0,0,0,0.03),0_4px_12px_-6px_rgba(0,0,0,0.05)] hover:border-black/[0.12] hover:shadow-[0_2px_4px_rgba(0,0,0,0.04),0_8px_20px_-8px_rgba(0,0,0,0.08)] px-4 py-3 flex items-center gap-3 transition"
        >
          <div className="w-9 h-9 rounded-full bg-[#FFEDD5] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-[18px] h-[18px] text-[#F97316]" strokeWidth={1.75} weight="regular" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13.5px] font-semibold text-black">
              Configura tu membrete profesional
            </div>
            <div className="text-[12px] text-black/55 mt-0.5">
              Falta subir{" "}
              <span className="font-semibold text-black/80">{missingBrand.join(" · ")}</span> para
              que aparezcan en tus certificados y recetas.
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#0071E3] group">
            Subir ahora
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} weight="regular" />
          </span>
        </button>
      )}

      {/* ── HEADER (eliminado por solicitud del usuario) ──────────── */}

      {/* ── WORKFLOW ROW ────────────────────────────────────────────── */}
      <div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <ConsultationsGauge
            pct={derived.gaugePct}
            done={derived.doneToday}
            total={derived.todayAppts.length}
            onClick={() => navigate("/doctor/agenda")}
          />
          <PrescriptionsSpectrum
            issued={prescriptions.length}
            recent={derived.rxCur}
            trend={derived.trendRx}
            periodLabel={periodLabel(workflowPeriod)}
            onClick={() => navigate("/doctor/recetas")}
          />
          <PaymentsCard paid={derived.paid} pending={derived.pending} periodLabel={periodLabel(workflowPeriod)} onClick={() => navigate("/doctor/finanzas")} />
          <ReferralsCard active={derived.refActive} total={derived.refTotal} onClick={() => navigate("/doctor/referidos")} />
          <DiseasesRow top={derived.topDx} max={derived.topDxMax} onClick={() => navigate("/doctor/pacientes")} />
        </div>
      </div>

      {/* ── HERO ROW ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
        {/* Right: Today's appointments (chat-like) */}
        <div className="col-span-12 apple-card flex flex-col min-h-0">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[15px] font-semibold text-black">
                Próximas citas
              </div>
              <div className="text-xs text-black/45 mt-0.5 flex items-center gap-2">
                {derived.todayAppts.length} hoy
                <span className="inline-flex -space-x-1">
                  {derived.todayAppts.slice(0, 3).map((a) => {
                    const p = patients.find((x) => x.id === a.patient_id);
                    return (
                      <span
                        key={a.id}
                        className="w-5 h-5 rounded-full bg-[#F2F3F5] border border-white text-[9px] font-bold text-black flex items-center justify-center"
                      >
                        {initials(p?.full_name || a.patient_name)}
                      </span>
                    );
                  })}
                </span>
              </div>
            </div>
            <Link
              to="/doctor/agenda"
              data-testid="dash-open-agenda"
              className="w-8 h-8 rounded-xl bg-[#F2F3F5] hover:bg-black/[0.06] flex items-center justify-center transition"
            >
              <ArrowUpRight className="w-4 h-4 text-black" strokeWidth={1.75} weight="regular" />
            </Link>
          </div>

          <div className="mt-4 h-px bg-black/[0.05]" />

          <div className="mt-2 flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
            {derived.todayAppts.length === 0 ? (
              <div className="text-center py-10 text-xs text-black/40">
                Sin citas hoy
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="dash-upcoming-table">
                <thead className="sticky top-0 bg-[#F5F5F7] z-[1]">
                  <tr className="text-left">
                    {["Hora", "Paciente", "Teléfono", "Correo", "Edad", "Consulta", ""].map(
                      (h) => (
                        <th
                          key={h || "spacer"}
                          className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {derived.todayAppts.map((a) => {
                    const p = patients.find((x) => x.id === a.patient_id);
                    const name = p?.full_name || a.patient_name || "Paciente";
                    const when = new Date(a.date);
                    const age = ageLabel(p?.birth_date);
                    const phone = p?.phone;
                    const email = p?.email;
                    return (
                      <tr
                        key={a.id}
                        onClick={() => navigate(`/doctor/pacientes/${a.patient_id}`)}
                        data-testid={`dash-upcoming-${a.id}`}
                        className="border-t border-black/[0.05] text-black transition group hover:bg-black/[0.02] cursor-pointer"
                      >
                        <td className="py-3 pr-3 whitespace-nowrap" data-testid={`dash-time-${a.id}`}>
                          <div className="flex flex-col leading-tight">
                            <span className="text-[15px] font-bold text-black tabular-nums">
                              {when.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false })}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-black/40 font-semibold">
                              {a.duration_min ? `${a.duration_min} min` : "—"}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-3">
                            {p?.photo_url ? (
                              <img
                                src={p.photo_url}
                                alt=""
                                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-[#F2F3F5] text-black text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                                {initials(name)}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold truncate max-w-[220px] text-[14px] text-black">
                                {name}
                              </div>
                              <div className="text-[11.5px] text-black/50 truncate max-w-[220px]">
                                {a.reason || "Consulta programada"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-[13px] text-black/80 whitespace-nowrap">
                          {phone ? (
                            <a
                              href={`tel:${phone}`}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`dash-phone-${a.id}`}
                              className="hover:text-[#0071E3] transition-colors"
                            >
                              {phone}
                            </a>
                          ) : (
                            <span className="text-black/30">—</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-[13px] text-black/80">
                          {email ? (
                            <a
                              href={`mailto:${email}`}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`dash-email-${a.id}`}
                              className="truncate inline-block max-w-[200px] align-middle hover:text-[#0071E3] transition-colors"
                            >
                              {email}
                            </a>
                          ) : (
                            <span className="text-black/30">—</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-[11px] font-semibold uppercase tracking-wider text-black/60 whitespace-nowrap" data-testid={`dash-age-${a.id}`}>
                          {age}
                        </td>
                        <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={async () => {
                              try {
                                if (a.status !== "in_progress") {
                                  await api.put(`/appointments/${a.id}`, {
                                    patient_id: a.patient_id,
                                    office_id: a.office_id,
                                    date: a.date,
                                    duration_min: a.duration_min,
                                    reason: a.reason,
                                    status: "in_progress",
                                  });
                                }
                              } catch (err) {
                                toast.error("No pude iniciar la consulta");
                              } finally {
                                navigate(`/doctor/consulta/${a.id}`);
                              }
                            }}
                            data-testid={`dash-start-${a.id}`}
                            title="Iniciar consulta"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#0071E3] text-white text-[11px] font-semibold hover:bg-[#0077ED] transition"
                          >
                            <Stethoscope className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                            Iniciar
                          </button>
                        </td>
                        <td className="py-3 pl-3 text-right whitespace-nowrap text-black/30">
                          <ArrowUpRight className="w-4 h-4 inline-block" strokeWidth={1.75} weight="regular" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Welcome modal — kept intact */}
      {welcome && (
        <Portal>
          <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-md p-4"
            data-testid="pharmacy-welcome-modal"
            onClick={dismissWelcome}
          >
            <div
              className="relative w-full max-w-md rounded-xl p-8 text-center bg-white shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={dismissWelcome}
                data-testid="pharmacy-welcome-close"
                className="absolute top-4 right-4 w-9 h-9 rounded-xl bg-black/5 hover:bg-black/10 flex items-center justify-center text-black/70"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-xl bg-black flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-white" strokeWidth={1.8} />
                </div>
              </div>
              <h2 className="text-2xl font-extrabold text-black tracking-tight">
                {welcome.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-black/70">
                {welcome.message}
              </p>
              <button
                onClick={dismissWelcome}
                data-testid="pharmacy-welcome-cta"
                className="mt-6 btn-primary"
              >
                ¡Genial, empecemos!
              </button>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function PeriodMenu({ value, onChange, variant = "ghost", testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const triggerCls =
    variant === "pill"
      ? "h-8 px-3 rounded-xl bg-white border border-black/10 hover:bg-black/[0.03] text-[12px] font-semibold text-black/70 flex items-center gap-1.5"
      : "h-9 px-3.5 rounded-md hover:bg-black/[0.04] text-[13px] font-semibold text-black flex items-center gap-2 transition";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerCls}
        data-testid={testId}
      >
        {variant === "pill" && (
          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
        )}
        {periodLabel(value)}
        <ChevronDown
          className={`w-3.5 h-3.5 ${variant === "pill" ? "text-black/40" : "text-black/50"} transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] z-40 min-w-[160px] rounded-xl border border-black/[0.08] bg-white shadow-lg p-1"
          data-testid={testId ? `${testId}-menu` : undefined}
        >
          {PERIOD_OPTIONS.map((opt) => {
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                data-testid={testId ? `${testId}-${opt.value}` : undefined}
                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] flex items-center justify-between transition ${
                  active
                    ? "bg-black text-white font-semibold"
                    : "text-black/80 hover:bg-black/[0.04]"
                }`}
              >
                <span>{opt.label}</span>
                {active && <span className="text-[10px]">●</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OfficeSwitcher({ offices, officeId, setOfficeId, doctorName }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selected = offices.find((o) => o.id === officeId);
  const label = selected?.name || `Consultorio de ${doctorName}`;
  const today = new Date().toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="office-switcher"
        className="group flex items-center gap-2 rounded-lg px-3 py-1.5 hover:bg-black/[0.04] transition"
      >
        <div className="text-center">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold text-black tracking-tight">
            <span>{label}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 text-black/50 transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </div>
          <div className="text-[11px] text-black/45 mt-0.5">{today}</div>
        </div>
      </button>
      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-[calc(100%+6px)] z-40 min-w-[240px] rounded-xl border border-black/[0.08] bg-white shadow-lg p-1"
          data-testid="office-switcher-menu"
        >
          <button
            type="button"
            onClick={() => {
              setOfficeId("");
              setOpen(false);
            }}
            data-testid="office-switcher-all"
            className={`w-full text-left px-3 py-2 rounded-lg text-[13px] flex items-center justify-between transition ${
              !officeId
                ? "bg-black text-white font-semibold"
                : "text-black/80 hover:bg-black/[0.04]"
            }`}
          >
            <span>Todos los consultorios</span>
            {!officeId && <span className="text-[10px]">●</span>}
          </button>
          {offices.length > 0 && (
            <div className="my-1 h-px bg-black/[0.06]" />
          )}
          {offices.map((o) => {
            const active = o.id === officeId;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  setOfficeId(o.id);
                  setOpen(false);
                }}
                data-testid={`office-switcher-${o.id}`}
                className={`w-full text-left px-3 py-2 rounded-lg text-[13px] flex items-center justify-between gap-2 transition ${
                  active
                    ? "bg-black text-white font-semibold"
                    : "text-black/80 hover:bg-black/[0.04]"
                }`}
              >
                <span className="truncate">{o.name}</span>
                {active && <span className="text-[10px]">●</span>}
              </button>
            );
          })}
          {offices.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-black/45">
              Aún no tienes consultorios registrados
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HeroStat({ value, trend, label, sub, accent }) {
  const positive = trend >= 0;
  return (
    <div>
      <div className="flex items-start gap-2">
        <div
          className="text-[36px] leading-none font-semibold text-black tracking-tight"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {value}
        </div>
        <div className="pt-2">
          <div
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${
              positive ? "text-emerald-600" : "text-red-600"
            }`}
          >
            <span>{positive ? "▲" : "▼"}</span>
            <span>{Math.abs(trend)}%</span>
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: accent }}
        />
        <div className="text-[13px] font-medium text-black">{label}</div>
      </div>
      <div className="text-[11px] text-black/40">{sub}</div>
    </div>
  );
}

function WaveChart({ buckets, maxAppts, maxRx, monthLabels }) {
  const w = 1000;
  const h = 130;
  const barsCount = buckets.length;
  const barSlot = w / barsCount;
  const barW = Math.max(1.5, barSlot * 0.55);

  // Smooth line points (7-day rolling average of appts)
  const smoothed = buckets.map((_, i) => {
    const from = Math.max(0, i - 3);
    const to = Math.min(barsCount - 1, i + 3);
    let sum = 0, n = 0;
    for (let k = from; k <= to; k++) { sum += buckets[k].appts; n++; }
    return n ? sum / n : 0;
  });
  const smoothMax = Math.max(1, ...smoothed);
  const linePts = smoothed.map((v, i) => ({
    x: i * barSlot + barSlot / 2,
    y: h - (v / smoothMax) * h * 0.7 - 20,
  }));

  // Build a cubic bezier smooth path
  const pathD = linePts.reduce((acc, p, i, arr) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = arr[i - 1];
    const cpX = (prev.x + p.x) / 2;
    return `${acc} C ${cpX} ${prev.y}, ${cpX} ${p.y}, ${p.x} ${p.y}`;
  }, "");

  return (
    <div className="mt-3 relative flex-1 flex flex-col min-h-0">
      <div className="relative w-full flex-1 min-h-[120px]">
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
        >
          {/* Bars — cada día una barra fina */}
          {buckets.map((b, i) => {
            const barH = (b.appts / maxAppts) * (h * 0.85);
            const rxH = (b.rx / maxRx) * (h * 0.3);
            const cx = i * barSlot + (barSlot - barW) / 2;
            return (
              <g key={i}>
                {barH > 0 && (
                  <rect
                    x={cx}
                    y={h - barH - 20}
                    width={barW}
                    height={barH}
                    fill={i > barsCount * 0.55 && i < barsCount * 0.75 ? "#C8F0BE" : "#E4F6DC"}
                    rx="1"
                  />
                )}
                {rxH > 0 && (
                  <rect
                    x={cx}
                    y={h - rxH - 20}
                    width={barW}
                    height={rxH}
                    fill={i > barsCount * 0.6 && i < barsCount * 0.78 ? "#F0B994" : "#F4D2B2"}
                    rx="1"
                    opacity="0.85"
                  />
                )}
              </g>
            );
          })}

          {/* Smooth wave line — trend */}
          <path
            d={pathD}
            fill="none"
            stroke="#ffffff"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="drop-shadow(0 1px 2px rgba(0,0,0,0.08))"
          />
          <path
            d={pathD}
            fill="none"
            stroke="#000000"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.55"
          />
        </svg>
      </div>

      {/* Month labels along X axis */}
      <div className="mt-1 relative h-4">
        {monthLabels.map((m) => (
          <span
            key={m.idx}
            className="absolute top-0 text-[11px] text-black/45 capitalize"
            style={{ left: `${(m.idx / barsCount) * 100}%` }}
          >
            {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ConsultationsGauge({ pct, done, total, onClick }) {
  const pending = Math.max(0, total - done);
  return (
    <AppleKpi
      testid="widget-consultas"
      icon={Stethoscope}
      title="Consultas hoy"
      description="Mantén el ritmo de tu día revisando qué consulta sigue."
      label="Programadas"
      value={`${done}/${total}`}
      footer={
        total === 0
          ? "Agenda vacía hoy"
          : pending === 0
          ? "Día completado"
          : `${pending} pendientes por atender`
      }
      onClick={onClick}
    />
  );
}

function PrescriptionsSpectrum({ issued, recent, trend, periodLabel = "30d", onClick }) {
  return (
    <AppleKpi
      testid="widget-recetas"
      icon={FileText}
      title="Recetas"
      description="Historial completo de prescripciones digitales emitidas."
      label="Total emitidas"
      value={issued}
      footer={`${recent} nuevas en ${periodLabel}`}
      onClick={onClick}
    />
  );
}


function PaymentsCard({ paid, pending, periodLabel = "Este mes", onClick }) {
  const paidFmt = `$${(paid || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 })}`;
  const pendingFmt = `$${(pending || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 })}`;
  return (
    <AppleKpi
      testid="widget-pagos"
      icon={Wallet}
      title="Cobros"
      description="Ingresos consolidados del período seleccionado."
      label={periodLabel}
      value={paidFmt}
      footer={`Pendiente por cobrar · ${pendingFmt}`}
      onClick={onClick}
    />
  );
}

function ReferralsCard({ active, total, onClick }) {
  return (
    <AppleKpi
      testid="widget-referidos"
      icon={Send}
      title="Referidos"
      description="Pacientes derivados a otros especialistas."
      label="Activos"
      value={active}
      footer={`${total} en el histórico total`}
      onClick={onClick}
    />
  );
}

function DiseasesRow({ top, max, onClick }) {
  const empty = !top || top.length === 0;
  const totalPatients = empty ? 0 : top.reduce((s, d) => s + (d.patients || 0), 0);
  return (
    <AppleKpi
      testid="widget-enfermedades"
      icon={Activity}
      title="Enfermedades"
      description="Diagnósticos más frecuentes de tu práctica."
      label="Diagnósticos únicos"
      value={empty ? 0 : top.length}
      footer={empty ? "Sin diagnósticos registrados" : `${totalPatients} pacientes afectados`}
      onClick={onClick}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function ageLabel(iso) {
  if (!iso) return "—";
  const b = new Date(iso);
  if (isNaN(b)) return "—";
  const now = new Date();
  let months = (now.getFullYear() - b.getFullYear()) * 12 + now.getMonth() - b.getMonth();
  if (now.getDate() < b.getDate()) months--;
  if (months < 12) return `${Math.max(0, months)} ${months === 1 ? "mes" : "meses"}`;
  const y = Math.floor(months / 12);
  return `${y} ${y === 1 ? "año" : "años"}`;
}


function formatBirthDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d)) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    const formatted = d.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return { formatted, age };
  } catch {
    return null;
  }
}
